import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { brands } from '@/mocks/products';

function BrandCard({ brand, index }: { brand: typeof brands[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative bg-white overflow-hidden cursor-pointer"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link to="/shop" className="block">
        {/* Image */}
        <div className="relative h-56 md:h-64 overflow-hidden">
          <img
            src={brand.image}
            alt={brand.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className={`absolute inset-0 bg-[#1C1C1C] transition-opacity duration-500 ${hovered ? 'opacity-40' : 'opacity-20'}`}></div>

          {/* Brand name overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <h3 className={`font-serif text-4xl md:text-5xl font-bold text-white transition-all duration-500 ${hovered ? 'scale-110' : 'scale-100'}`}>
              {brand.name}
            </h3>
          </div>

          {/* Origin badge */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#1C1C1C]">
            {brand.origin}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#ec3145]">
              {brand.specialty}
            </span>
            <span className="text-xs text-[#1C1C1C]/30">
              {brand.productCount}+ პროდუქტი
            </span>
          </div>
          <p className="text-sm text-[#1C1C1C]/50 leading-relaxed mb-5">
            {brand.description}
          </p>
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1C1C1C] group-hover:text-[#ec3145] transition-colors duration-300">
            <span>პროდუქტების ნახვა</span>
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function BrandsPage() {
  return (
    <div className="font-sans bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[480px] md:min-h-[560px] bg-[#1C1C1C] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20brand%20logos%20display%20on%20elegant%20marble%20wall%20luxury%20showroom%20interior%20warm%20golden%20lighting%20professional%20architecture%20photography%20wide%20angle&width=1920&height=800&seq=phoenix-brands-hero&orientation=landscape"
            alt="ბრენდები"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20">
          <div className="max-w-2xl">
            <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-4">
              პარტნიორები
            </span>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              მსოფლიოს<br />
              <span className="text-[#ec3145]">წამყვანი ბრენდები</span>
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-lg">
              Phoenix არის მსოფლიოს ყველაზე პრესტიჟული სამშენებლო მასალების ბრენდების ოფიციალური დისტრიბუტორი საქართველოში. თითოეული ბრენდი შერჩეულია ხარისხის, ინოვაციისა და საიმედოობის კრიტერიუმებით.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-[#F5F2EE] border-b border-[#1C1C1C]/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '7', label: 'ბრენდი' },
              { value: '2,950+', label: 'პროდუქტი' },
              { value: '4', label: 'ქვეყანა' },
              { value: '25+', label: 'წელი პარტნიორობა' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-serif text-3xl md:text-4xl font-bold text-[#1C1C1C]">{stat.value}</div>
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#1C1C1C]/30 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="bg-[#F5F2EE] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {brands.map((brand, i) => (
              <BrandCard key={brand.name} brand={brand} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Why These Brands */}
      <section className="bg-[#1C1C1C] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-4">
                რატომ ეს ბრენდები
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight mb-6">
                მხოლოდ საუკეთესოები
              </h2>
              <p className="text-white/50 text-base leading-relaxed mb-8">
                ჩვენი ბრენდ-პარტნიორების შერჩევა მკაცრი კრიტერიუმებით ხდება. თითოეული ბრენდი უნდა აკმაყოფილებდეს საერთაშორისო სტანდარტებს, იყოს ინოვაციური და გქონდეს დამტკიცებული რეპუტაცია მსოფლიო მასშტაბით.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  { icon: 'ri-award-line', title: 'საერთაშორისო სერტიფიკატები', desc: 'ISO 9001, CE მარკირება, ეკოლოგიური სტანდარტები' },
                  { icon: 'ri-shield-check-line', title: 'ოფიციალური გარანტია', desc: 'ყველა პროდუქტზე მწარმოებლის სრული გარანტია' },
                  { icon: 'ri-global-line', title: 'გლობალური ხარისხი', desc: 'იტალია, გერმანია, ესპანეთი, დანია — ევროპის საუკეთესოები' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#ec3145]/20 flex-shrink-0">
                      <i className={`${item.icon} text-[#ec3145]`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm mb-1">{item.title}</h4>
                      <p className="text-xs text-white/40 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[400px] md:h-[500px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20construction%20materials%20brands%20collection%20display%20elegant%20showroom%20with%20marble%20stone%20and%20wood%20samples%20warm%20golden%20lighting%20luxury%20interior%20photography&width=700&height=500&seq=phoenix-brands-side&orientation=portrait"
                alt="ბრენდების კოლექცია"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F5F2EE] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
            გაიცანით პროდუქტები ახლო მიმოხილვით
          </h2>
          <p className="text-[#1C1C1C]/50 text-base max-w-xl mx-auto mb-8">
            ეწვიეთ ჩვენს შოურუმს თბილისში და პირადად გაეცანით ყველა ბრენდის პრემიუმ პროდუქტებს.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 bg-[#ec3145] text-white font-semibold px-8 py-4 hover:bg-[#c9283a] transition-colors duration-300 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line"></i>
              მაღაზიაში გადასვლა
            </Link>
            <a
              href="tel:+995599872020"
              className="inline-flex items-center justify-center gap-2 border border-[#1C1C1C]/20 text-[#1C1C1C] font-semibold px-8 py-4 hover:bg-[#1C1C1C] hover:text-white transition-colors duration-300 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              შოურუმის ვიზიტის დაჯავშნა
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}